import { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import PublicMenuPage from "./PublicMenuPage";
import SlideshowPublicPage from "./SlideshowPublicPage";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CalendarPublicPage = () => {
  const { calendarId } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [contentData, setContentData] = useState(null);
  const [refreshInterval, setRefreshInterval] = useState(30); // Check every 30 seconds

  const fetchCalendarContent = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/public/calendar/${calendarId}`);
      setContentData(response.data);
      setError(null);
    } catch (err) {
      console.error("Error fetching calendar content:", err);
      setError("No se pudo cargar el contenido del calendario");
    } finally {
      setLoading(false);
    }
  }, [calendarId]);

  useEffect(() => {
    fetchCalendarContent();
    
    // Set up periodic refresh to check for schedule changes
    const interval = setInterval(fetchCalendarContent, refreshInterval * 1000);
    return () => clearInterval(interval);
  }, [fetchCalendarContent, refreshInterval]);

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-white text-xl animate-pulse">Cargando...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-red-500 text-xl">{error}</div>
      </div>
    );
  }

  // Handle suspended account
  if (contentData?.suspended) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-stone-900 to-stone-800 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="text-6xl mb-4">⚠️</div>
          <h1 className="text-2xl font-bold text-white mb-2">Servicio Suspendido</h1>
          <p className="text-stone-400">{contentData.suspension_message}</p>
        </div>
      </div>
    );
  }

  // Handle no content scheduled
  if (contentData?.no_content) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-stone-900 to-stone-800 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="text-6xl mb-4">📅</div>
          <h1 className="text-2xl font-bold text-white mb-2">{contentData.calendar_name || "Calendario"}</h1>
          <p className="text-stone-400">{contentData.message}</p>
        </div>
      </div>
    );
  }

  // Render the appropriate content based on type
  if (contentData?.type === "menu" && contentData?.content) {
    // Render menu directly with the content data
    return <PublicMenuPage menuData={contentData.content} />;
  }

  if (contentData?.type === "slideshow" && contentData?.content) {
    // Render slideshow directly with the content data
    return <SlideshowPublicPage slideshowData={contentData.content} />;
  }

  // Fallback - should not happen
  return (
    <div className="min-h-screen bg-black flex items-center justify-center">
      <div className="text-white text-xl">Sin contenido disponible</div>
    </div>
  );
};

export default CalendarPublicPage;
